import React from 'react';
import { SIDEBAR_WIDTH } from './style.js';

/** Owns only sidebar geometry, pointer capture and resize subscriptions. */
const clampSidebarWidth = (value, maximum) => Math.max(SIDEBAR_WIDTH.min, Math.min(value, maximum));

export default function useSidebarResize(surfaceRef, enabled, showCanvasPanel, canvasFullscreenOpen) {
  const [width, setWidth] = React.useState(SIDEBAR_WIDTH.initial);
  const [maximum, setMaximum] = React.useState(SIDEBAR_WIDTH.max);
  const drag = React.useRef(null);
  const finish = React.useCallback((event) => {
    const active = drag.current;
    if (!active || (event && event.pointerId !== active.pointerId)) return;
    drag.current = null;
    if (active.target.hasPointerCapture?.(active.pointerId)) active.target.releasePointerCapture(active.pointerId);
  }, []);
  React.useEffect(() => {
    if (!enabled) { finish(); return undefined; }
    const surface = surfaceRef.current;
    const panel = surface?.querySelector('.flare-chat-canvas-panel-host');
    const measure = () => {
      const available = (surface?.getBoundingClientRect().width ?? 0)
        - (panel?.getBoundingClientRect().width ?? 0) - SIDEBAR_WIDTH.mainMin;
      setMaximum(clampSidebarWidth(available, SIDEBAR_WIDTH.max));
    };
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (surface) observer?.observe(surface);
    if (panel) observer?.observe(panel);
    window.addEventListener('resize', measure);
    measure();
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); finish(); };
  }, [surfaceRef, enabled, showCanvasPanel, canvasFullscreenOpen, finish]);
  const move = React.useCallback((event) => {
    const active = drag.current;
    if (!active || event.pointerId !== active.pointerId) return;
    setWidth(clampSidebarWidth(active.width + event.clientX - active.x, maximum));
  }, [maximum]);
  const start = React.useCallback((event) => {
    if (!enabled || event.button !== 0 || drag.current) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, target: event.currentTarget, x: event.clientX,
      width: clampSidebarWidth(width, maximum) };
  }, [enabled, width, maximum]);
  return { width: clampSidebarWidth(width, maximum), maximum, start, move, finish };
}
